import { format } from 'date-fns';
import { TFunction } from 'i18next';

import { formatCurrencyDecimals, formatNumber } from 'utils/formats';

import {
  BeneficiarySocietyChartData,
  BeneficiarySocietyChartOptions,
} from '../types/BeneficiarySocietyTypes';

export const chartOptions = (
  t: TFunction,
  data: BeneficiarySocietyChartData,
  options?: BeneficiarySocietyChartOptions
) => {
  const allData = data?.simulatorSocietyValue
    ? [...data.incentivesValues, data.simulatorSocietyValue]
    : [...data.incentivesValues];

  const societyValuesData = allData?.reduce<Record<string, number>>(
    (acc, curr) => {
      acc[curr?.incentive.toString()] = curr?.value || 0;
      return acc;
    },
    {}
  );

  const pendingValues = data?.vestingValues.reduce<Record<string, number>>(
    (acc, curr) => {
      acc[format(new Date(curr?.date), 'dd-MM-yyyy')] = curr?.pending || 0;
      return acc;
    },
    {}
  );

  return {
    responsive: true,
    plugins: {
      legend: {
        display: !!options?.legend,
        position: 'top' as const,
      },
      title: {
        display: !!options?.title,
        text: options?.title,
      },
      filler: {
        propagate: false,
      },
      tooltip: {
        displayColors: false,
        callbacks: {
          label: (context: { datasetIndex: number; raw: number }) => {
            if (
              context.datasetIndex === 2 &&
              context.raw !== data?.simulatorSocietyValue?.incentive
            ) {
              return '';
            }
            if (context.datasetIndex === 1) {
              return `${t(
                'IncentiveValueConsolidated'
              )}: ${formatCurrencyDecimals(context.raw)}`;
            }
            return `${t('IncentiveValueEstimated')}: ${formatCurrencyDecimals(
              context.raw
            )}`;
          },
          afterLabel: (context: {
            datasetIndex: number;
            raw: number;
            label: string;
          }) => {
            if (
              context.datasetIndex === 2 &&
              context.raw !== data?.simulatorSocietyValue?.incentive
            ) {
              return '';
            }
            if (context.datasetIndex === 1) {
              const v = formatCurrencyDecimals(
                pendingValues?.[format(new Date(context.label), 'dd-MM-yyyy')]
              );
              return `${t('ConsolidatedValuePending')}: ${v}`;
            }
            const v = formatNumber(societyValuesData?.[context.raw]);
            return `${t('SocietyValueEstimated')}: ${v}€`;
          },
        },
      },
    },
    tooltips: {
      enabled: true,
      backgroundColor: '#eff6ff',
      titleFontSize: 13,
      titleFontColor: '#6783b8',
      titleMarginBottom: 6,
      bodyFontColor: '#9eaecf',
      bodyFontSize: 12,
      bodySpacing: 4,
      yPadding: 10,
      xPadding: 10,
      footerMarginTop: 0,
      displayColors: false,
    },
    showLine: true,
    spanGaps: true,
    response: true,
    scales: {
      x: {
        type: 'time' as const,
        time: {
          unit: 'month' as const,
        },
      },
    },
    ...options,
  };
};

export const convertData = (
  t: TFunction,
  values?: BeneficiarySocietyChartData
) => {
  if (!values) {
    return {};
  }
  const allValues = values?.simulatorSocietyValue
    ? [
        ...values.incentivesValues,
        ...values.vestingValues,
        values.simulatorSocietyValue,
      ]
    : [...values.incentivesValues, ...values.vestingValues];
  const sortedValues = allValues.sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return dateA.getTime() - dateB.getTime();
  });
  const filteredValues = sortedValues.reduce<
    Array<{ date: Date; incentive: number; value: number }>
  >((acc, curr, index) => {
    if (index === 0) acc.push(curr);
    else if (
      new Date(sortedValues[index - 1].date).getTime() !==
      new Date(curr.date).getTime()
    )
      acc.push(curr);
    return acc;
  }, []);

  const incentivesData = filteredValues.map((curr) => {
    const value = values?.incentivesValues?.find(
      (elem) => new Date(curr.date).getTime() === new Date(elem.date).getTime()
    );
    return value ? value.incentive : null;
  });

  const vestingData = filteredValues.map((curr) => {
    const value = values?.vestingValues?.find(
      (elem) => new Date(curr.date).getTime() === new Date(elem.date).getTime()
    );
    return value ? value.incentive : null;
  });

  const lastIncentive = values.incentivesValues.reduce((acc, curr) => {
    if (new Date(curr.date) > new Date(acc.date)) return curr;
    return acc;
  }, values.incentivesValues[0]);

  const simulatorData = filteredValues.map((curr) => {
    if (!values?.simulatorSocietyValue) return null;
    if (
      new Date(lastIncentive.date).getTime() === new Date(curr.date).getTime()
    )
      return lastIncentive.incentive;
    if (
      new Date(values?.simulatorSocietyValue?.date).getTime() ===
      new Date(curr.date).getTime()
    )
      return values?.simulatorSocietyValue?.incentive;
    return null;
  });

  const chartData = {
    labels: filteredValues?.map((elem) => new Date(elem.date)),
    datasets: [
      {
        label: t('IncentiveValueLegend'),
        data: incentivesData,
        fill: false,
        borderColor: '#6576ff',
        backgroundColor: '#6576ff',
        tension: 0,
        pointHoverRadius: 10,
        pointRadius: 2,
      },
      {
        label: t('ConsolidatedValueLegend'),
        data: vestingData,
        fill: false,
        borderColor: '#20c997',
        backgroundColor: '#20c997',
        tension: 0.1,
        pointHoverRadius: 10,
        pointRadius: 2,
      },
      {
        label: t('SimulatedValue'),
        data: simulatorData,
        borderDash: [2, 4],
        fill: false,
        borderColor: '#6576ff',
        backgroundColor: '#6576ff50',
        tension: 0.1,
        pointHoverRadius: 10,
        pointRadius: 2,
      },
    ],
  };
  return chartData;
};
